import { GaussianBlurBackgroundProcessor, ImageFit, VirtualBackgroundProcessor } from '@twilio/video-processors';
import type { Track, TrackProcessor, VideoProcessorOptions } from 'livekit-client';

export type BackgroundEffect = { type: 'blur'; radius: number } | { type: 'image'; path: string };

const ASSETS_PATH = '/twilio-vp/';
const FALLBACK_FRAME_RATE = 30;
const MODEL_LOAD_TIMEOUT_MS = 20_000;

// Twilio 3.2.0 has no destroy(): each processor instance spawns a Worker that is never terminated,
// and all instances of a kind talk to the first one anyway (module-level comlink wrap). Keep one per kind.
let blurProcessor: Promise<GaussianBlurBackgroundProcessor> | undefined;
let imageProcessor: Promise<VirtualBackgroundProcessor> | undefined;

async function withLoadedModel<T extends GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor>(processor: T) {
    await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new Error('Background model load timed out'));
        }, MODEL_LOAD_TIMEOUT_MS);
        processor
            .loadModel()
            .then(resolve, reject)
            .finally(() => {
                clearTimeout(timer);
            });
    });
    return processor;
}

async function loadImage(path: string) {
    const image = new Image();
    image.src = path;
    await image.decode();
    return image;
}

async function prepareProcessor(effect: BackgroundEffect) {
    if (effect.type === 'blur') {
        blurProcessor ??= withLoadedModel(
            new GaussianBlurBackgroundProcessor({ assetsPath: ASSETS_PATH, useWebWorker: true })
        ).catch((error: unknown) => {
            blurProcessor = undefined;
            throw error;
        });
        const processor = await blurProcessor;
        processor.blurFilterRadius = effect.radius;
        return processor;
    }

    const image = await loadImage(effect.path);
    if (!imageProcessor) {
        imageProcessor = withLoadedModel(
            new VirtualBackgroundProcessor({
                assetsPath: ASSETS_PATH,
                backgroundImage: image,
                fitType: ImageFit.Cover,
                useWebWorker: true,
            })
        ).catch((error: unknown) => {
            imageProcessor = undefined;
            throw error;
        });
        return imageProcessor;
    }
    const processor = await imageProcessor;
    processor.backgroundImage = image;
    return processor;
}

export class BackgroundEffectProcessor implements TrackProcessor<Track.Kind.Video> {
    readonly name = 'background-effect';
    readonly processedTrack: MediaStreamTrack;
    private readonly canvas = document.createElement('canvas');
    private processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor | undefined;
    private video: HTMLVideoElement | undefined;
    private ticker: Worker | undefined;
    private ticking = false;
    private frameIntervalMs = 1000 / FALLBACK_FRAME_RATE;
    private running = false;
    private busy = false;
    private frameSkipped = false;
    private cancelScheduledFrame: (() => void) | undefined;

    constructor() {
        const [track] = this.canvas.captureStream().getVideoTracks();
        if (!track) throw new Error('Canvas capture is not supported');
        this.processedTrack = track;
    }

    async setEffect(effect: BackgroundEffect) {
        this.processor = await prepareProcessor(effect);
    }

    async init({ track, element }: VideoProcessorOptions) {
        const { width, height, frameRate } = track.getSettings();
        if (width && height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }
        this.frameIntervalMs = 1000 / (frameRate ?? FALLBACK_FRAME_RATE);
        this.video = element instanceof HTMLVideoElement ? element : document.createElement('video');
        await playSource(this.video, track);

        this.running = true;
        document.addEventListener('visibilitychange', this.reschedule);
        this.scheduleFrame();
    }

    async restart({ track }: VideoProcessorOptions) {
        if (this.video) await playSource(this.video, track);
    }

    destroy() {
        this.running = false;
        this.cancelFrame();
        this.ticker?.terminate();
        this.ticker = undefined;
        document.removeEventListener('visibilitychange', this.reschedule);
        this.processedTrack.stop();
        return Promise.resolve();
    }

    private readonly renderFrame = () => {
        this.cancelScheduledFrame = undefined;
        if (!this.running) return;
        this.scheduleFrame();
        this.processCurrentFrame();
    };

    private processCurrentFrame() {
        const { video, processor, canvas } = this;
        if (!this.running || !video || !processor || video.videoWidth === 0) return;
        if (this.busy) {
            this.frameSkipped = true;
            return;
        }
        if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
        if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;

        this.busy = true;
        processor
            .processFrame(video, canvas)
            .catch((error: unknown) => {
                console.error(error);
            })
            .finally(() => {
                this.busy = false;
                if (this.frameSkipped) {
                    this.frameSkipped = false;
                    this.processCurrentFrame();
                }
            });
    }

    private scheduleFrame() {
        const video = this.video;
        if (!video) return;
        if (document.visibilityState === 'visible') {
            const request = video.requestVideoFrameCallback(this.renderFrame);
            this.cancelScheduledFrame = () => {
                video.cancelVideoFrameCallback(request);
            };
            return;
        }

        this.ticker ??= this.createTicker();
        const ticker = this.ticker;
        this.ticking = true;
        ticker.postMessage(this.frameIntervalMs);
        this.cancelScheduledFrame = () => {
            this.ticking = false;
            ticker.postMessage(null);
        };
    }

    private createTicker() {
        const ticker = new Worker(new URL('frame-ticker-worker.ts', import.meta.url), { type: 'module' });
        ticker.addEventListener('message', () => {
            if (this.ticking) this.processCurrentFrame();
        });
        return ticker;
    }

    private cancelFrame() {
        this.cancelScheduledFrame?.();
        this.cancelScheduledFrame = undefined;
    }

    private readonly reschedule = () => {
        this.cancelFrame();
        if (this.running) this.scheduleFrame();
    };
}

async function playSource(video: HTMLVideoElement, track: MediaStreamTrack) {
    const currentTrack = video.srcObject instanceof MediaStream ? video.srcObject.getVideoTracks()[0] : undefined;
    if (currentTrack !== track) video.srcObject = new MediaStream([track]);
    video.muted = true;
    video.playsInline = true;
    await video.play();
}
